import React, {useMemo} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Text} from '@forecast-it/design-system';
import {NovaAgentDraft} from '../state/useNovaAgentDraft';
import {PromptTarget} from '../state/useNovaPromptState';
import {NovaVariable} from '../shared/novaVariableLibrary';
import {MonospacePromptEditor} from './MonospacePromptEditor';
import {NovaMcpTools} from './NovaMcpTools';
import {NovaWorkflowVariableLibrary, StudioWorkflowVariableGroup, validateWorkflowInput} from './NovaWorkflowVariableLibrary';
import {NovaInputIssues} from './NovaInputIssues';
import {useAgentMdTargetClaim} from './usePromptTargetClaim';
import {useNovaMcpTools} from '../shared/useNovaMcpTools';

// The same rhythm as a task agent's agent.md tab (design system spacing step 800), so switching
// between the two kinds of agent doesn't shift the layout.
const Container = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-height: 0;
	gap: 32px;
`;

// Tall enough to work with a multi-section agent.md without immediately needing to scroll.
const EDITOR_HEIGHT = 480;

interface Props {
	draft: NovaAgentDraft;
	variableGroups: StudioWorkflowVariableGroup[];
	declarationFailed: boolean;
	// The declaration has not been read yet: no token is flagged and the library claims no count.
	declarationLoading?: boolean;
	// Reads the declaration again after a failed read.
	onRetryDeclaration?: () => void;
	// The workflow's ready files, each citable as a {file:...} variable, among the library variables.
	variables: NovaVariable[];
}

// An agentic workflow's agent.md: its optional standing context, added to the System prompt on every
// run. In order: what agent.md is for, the Variable library and the MCP tools (each naming agent.md as
// where a click inserts), then the editor. Customer data and files are disabled here with the reason,
// since a standing input applies unchanged on every run; run parameters and tools stay insertable. A
// customer-data reference typed in by hand is flagged below the editor rather than blocked.
//
// Nothing on this tab is required. An empty agent.md saves as empty and reports nothing at all.
export function NovaWorkflowAgentMdTab({
	draft,
	variableGroups,
	declarationFailed,
	declarationLoading = false,
	onRetryDeclaration,
	variables,
}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	const {values, setValue, setActiveTarget, activeTarget, insertTokenAtCursor, lastInsertion} = draft.promptState;
	useAgentMdTargetClaim(draft.promptState);
	const mcpTools = useNovaMcpTools();
	const declarationUnknown = declarationFailed || declarationLoading;

	const fileVariables = useMemo(() => variables.filter(variable => variable.scope === 'FILES'), [variables]);
	const issues = useMemo(
		() =>
			validateWorkflowInput(
				values.agentMd,
				PromptTarget.AgentMd,
				variableGroups,
				fileVariables,
				declarationUnknown,
				mcpTools
			),
		[values.agentMd, variableGroups, fileVariables, declarationUnknown, mcpTools]
	);
	const targetLabel = formatMessage({id: 'nova.variableLibrary.targetAgentMd'});

	return (
		<Container data-cy="nova-agent-md-tab" data-testid="nova-agent-md-tab">
			<Text color="auxiliary" data-cy="nova-agent-md-explainer">
				{formatMessage({id: 'nova.agentMd.explainer'})}
			</Text>
			<NovaWorkflowVariableLibrary
				variableGroups={variableGroups}
				fileVariables={fileVariables}
				activeTarget={activeTarget}
				loadFailed={declarationFailed}
				loading={declarationLoading}
				onRetry={onRetryDeclaration}
				targetLabel={targetLabel}
				onInsert={token => insertTokenAtCursor(token)}
			/>
			<NovaMcpTools
				mcpTools={mcpTools}
				onRetry={mcpTools.retry}
				targetLabel={targetLabel}
				onInsert={token => insertTokenAtCursor(token)}
			/>
			<MonospacePromptEditor
				dataCy="nova-agent-md"
				target={PromptTarget.AgentMd}
				value={values.agentMd}
				height={EDITOR_HEIGHT}
				placeholder={formatMessage({id: 'nova.agentMd.placeholder'})}
				helperText={formatMessage({id: 'nova.agentMd.helperText'})}
				onChange={(value: string) => setValue(PromptTarget.AgentMd, value)}
				onActivate={setActiveTarget}
				insertion={lastInsertion?.target === PromptTarget.AgentMd ? lastInsertion : null}
				caption={
					activeTarget === PromptTarget.AgentMd ? formatMessage({id: 'nova.prompt.insertTargetHint'}) : undefined
				}
				issues={issues.length > 0 ? <NovaInputIssues issues={issues} dataCy="nova-agent-md-issues" /> : undefined}
			/>
		</Container>
	);
}
